import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getAdminSessionId } from "@/lib/adminSession";
import { restaurantsApi } from "@/lib/restaurantsApi";
import type { Tables } from "@/integrations/supabase/types";

export type Restaurant = Tables<"restaurants">;
export type Menu = Tables<"menus">;
export type Dish = Tables<"dishes">;
export type Component = Tables<"components">;
export type Ingredient = Tables<"ingredients">;
export type Supplier = Tables<"suppliers">;
export type RestaurantUser = Tables<"restaurant_users">;
export type RestaurantSettings = Tables<"restaurant_settings">;

export interface RestaurantListItem {
  id: string;
  name: string;
  status: "active" | "inactive";
  menuCount: number;
  dishCount: number;
  userCount: number;
  lastUpdated: string;
}

interface RestaurantStore {
  list: RestaurantListItem[];
  loading: boolean;
  refresh: () => Promise<void>;
  addRestaurant: (r: { name: string; address: string; email: string; contact: string; status: string }) => Promise<string>;
  updateRestaurant: (id: string, data: Partial<Restaurant>) => Promise<void>;
  deleteRestaurant: (id: string) => Promise<void>;
  fetchRestaurant: (id: string) => Promise<Restaurant | null>;
  // Core entity CRUD
  fetchMenus: (rid: string) => Promise<Menu[]>;
  addMenu: (rid: string, m: { name: string; schedule: string }) => Promise<void>;
  deleteMenu: (rid: string, mid: string) => Promise<void>;
  fetchDishes: (rid: string) => Promise<Dish[]>;
  addDish: (rid: string, d: { name: string; category: string }) => Promise<void>;
  deleteDish: (rid: string, did: string) => Promise<void>;
  fetchComponents: (rid: string) => Promise<Component[]>;
  addComponent: (rid: string, c: { name: string }) => Promise<void>;
  deleteComponent: (rid: string, cid: string) => Promise<void>;
  fetchIngredients: (rid: string) => Promise<Ingredient[]>;
  addIngredient: (rid: string, ing: { name: string }) => Promise<void>;
  deleteIngredient: (rid: string, iid: string) => Promise<void>;
  fetchSuppliers: (rid: string) => Promise<Supplier[]>;
  addSupplier: (rid: string, s: { name: string; email: string; notes?: string }) => Promise<void>;
  deleteSupplier: (rid: string, sid: string) => Promise<void>;
  fetchUsers: (rid: string) => Promise<RestaurantUser[]>;
  addUser: (rid: string, u: { username: string; role: string }) => Promise<void>;
  deleteUser: (rid: string, uid: string) => Promise<void>;
  toggleUserStatus: (rid: string, uid: string) => Promise<void>;
  fetchSettings: (rid: string) => Promise<RestaurantSettings | null>;
  saveSettings: (rid: string, s: { language: string; integration_key: string }) => Promise<void>;
  // Junction table CRUD
  fetchMenuDishes: (menuId: string) => Promise<Dish[]>;
  addMenuDish: (menuId: string, dishId: string) => Promise<void>;
  removeMenuDish: (menuId: string, dishId: string) => Promise<void>;
  fetchDishComponents: (dishId: string) => Promise<Component[]>;
  addDishComponent: (dishId: string, componentId: string) => Promise<void>;
  removeDishComponent: (dishId: string, componentId: string) => Promise<void>;
  fetchComponentIngredients: (componentId: string) => Promise<Ingredient[]>;
  addComponentIngredient: (componentId: string, ingredientId: string) => Promise<void>;
  removeComponentIngredient: (componentId: string, ingredientId: string) => Promise<void>;
  fetchIngredientSuppliers: (ingredientId: string) => Promise<Supplier[]>;
  addIngredientSupplier: (ingredientId: string, supplierId: string) => Promise<void>;
  removeIngredientSupplier: (ingredientId: string, supplierId: string) => Promise<void>;
  // Dashboard stats
  getTotalDishes: () => Promise<number>;
  getTotalUsers: () => Promise<number>;
}

const RestaurantContext = createContext<RestaurantStore | null>(null);

export function RestaurantProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<RestaurantListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    if (!getAdminSessionId()) { setList([]); setLoading(false); return; }
    try {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_restaurants_with_counts", admin_session_id: getAdminSessionId() },
      });
      const rows = (data?.data || []) as Array<{
        id: string; name: string; status: string; updated_at: string;
        menuCount: number; dishCount: number; userCount: number;
      }>;
      setList(rows.map((r) => ({
        id: r.id,
        name: r.name,
        status: r.status as "active" | "inactive",
        menuCount: r.menuCount || 0,
        dishCount: r.dishCount || 0,
        userCount: r.userCount || 0,
        lastUpdated: (r.updated_at || "").slice(0, 10),
      })));
    } catch {
      // swallow — UI will show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  // Only auto-refresh on routes that actually display the restaurants list.
  useEffect(() => {
    const shouldLoad = () =>
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin/restaurants");
    if (shouldLoad()) refresh();
    else setLoading(false);
  }, [refresh]);

  // --- Core restaurant CRUD (routed through manage-restaurants Edge Function) ---
  const addRestaurant = useCallback(async (r: { name: string; address: string; email: string; contact: string; status: string }) => {
    const created = await restaurantsApi.create(r);
    // Optimistic insert — avoids full refetch
    setList((prev) => [
      {
        id: created.id,
        name: created.name,
        status: (created.status || "active") as "active" | "inactive",
        menuCount: 0,
        dishCount: 0,
        userCount: 0,
        lastUpdated: (created.updated_at || new Date().toISOString()).slice(0, 10),
      },
      ...prev,
    ]);
    return created.id as string;
  }, []);

  const updateRestaurant = useCallback(async (id: string, data: Partial<Restaurant>) => {
    await restaurantsApi.update(id, data as Record<string, unknown>);
    setList((prev) => prev.map((r) => r.id === id ? {
      ...r,
      name: (data as any).name ?? r.name,
      status: ((data as any).status ?? r.status) as "active" | "inactive",
      lastUpdated: new Date().toISOString().slice(0, 10),
    } : r));
  }, []);

  const deleteRestaurant = useCallback(async (id: string) => {
    await restaurantsApi.remove(id);
    setList((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const fetchRestaurant = useCallback(async (id: string) => {
    return (await restaurantsApi.get(id)) as Restaurant | null;
  }, []);
  const fetchMenus = useCallback(async (rid: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_menus", restaurant_id: rid, admin_session_id: adminSession },
      });
      return (data?.data || []) as Menu[];
    }
    const { data } = await supabase.from("menus").select("*").eq("restaurant_id", rid).order("created_at");
    return data || [];
  }, []);
  const addMenu = useCallback(async (rid: string, m: { name: string; schedule: string }) => {
    await supabase.from("menus").insert({ restaurant_id: rid, name: m.name, schedule: m.schedule });
    setList((prev) => prev.map((r) => r.id === rid ? { ...r, menuCount: r.menuCount + 1, lastUpdated: new Date().toISOString().slice(0, 10) } : r));
  }, []);
  const deleteMenu = useCallback(async (rid: string, mid: string) => {
    await supabase.from("menus").delete().eq("id", mid);
    setList((prev) => prev.map((r) => r.id === rid ? { ...r, menuCount: Math.max(0, r.menuCount - 1), lastUpdated: new Date().toISOString().slice(0, 10) } : r));
  }, []);

  // --- Dishes ---
  const fetchDishes = useCallback(async (rid: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_dishes", restaurant_id: rid, admin_session_id: adminSession },
      });
      return (data?.data || []) as Dish[];
    }
    const { data } = await supabase.from("dishes").select("*").eq("restaurant_id", rid).order("created_at");
    return data || [];
  }, []);
  const addDish = useCallback(async (rid: string, d: { name: string; category: string }) => {
    await supabase.from("dishes").insert({ restaurant_id: rid, name: d.name, category: d.category });
    setList((prev) => prev.map((r) => r.id === rid ? { ...r, dishCount: r.dishCount + 1, lastUpdated: new Date().toISOString().slice(0, 10) } : r));
  }, []);
  const deleteDish = useCallback(async (rid: string, did: string) => {
    // Check if dish is still in any menu
    const { count } = await supabase.from("menu_dishes").select("id", { count: "exact", head: true }).eq("dish_id", did);
    if (!count || count === 0) {
      await supabase.from("dish_components").delete().eq("dish_id", did);
      await supabase.from("dish_allergens").delete().eq("dish_id", did);
      await supabase.from("dish_suppliers").delete().eq("dish_id", did);
    }
    await supabase.from("dishes").delete().eq("id", did);
    setList((prev) => prev.map((r) => r.id === rid ? { ...r, dishCount: Math.max(0, r.dishCount - 1), lastUpdated: new Date().toISOString().slice(0, 10) } : r));
  }, []);

  // --- Components ---
  const fetchComponents = useCallback(async (rid: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_components", restaurant_id: rid, admin_session_id: adminSession },
      });
      return (data?.data || []) as Component[];
    }
    const { data } = await supabase.from("components").select("*").eq("restaurant_id", rid).order("created_at");
    return data || [];
  }, []);
  const addComponent = useCallback(async (rid: string, c: { name: string }) => {
    await supabase.from("components").insert({ restaurant_id: rid, name: c.name });
  }, []);
  const deleteComponent = useCallback(async (_rid: string, cid: string) => {
    // Check if component is still linked to any dish
    const { count } = await supabase.from("dish_components").select("id", { count: "exact", head: true }).eq("component_id", cid);
    if (!count || count === 0) {
      await supabase.from("component_ingredients").delete().eq("component_id", cid);
    }
    await supabase.from("components").delete().eq("id", cid);
  }, []);

  // --- Suppliers (via secure Edge Function) ---
  const fetchSuppliers = useCallback(async (rid: string) => {
    const { suppliersApi } = await import("@/lib/suppliersApi");
    return await suppliersApi.list(rid);
  }, []);
  const addSupplier = useCallback(async (rid: string, s: { name: string; email: string; notes?: string }) => {
    const { suppliersApi } = await import("@/lib/suppliersApi");
    await suppliersApi.create({ name: s.name, email: s.email, notes: s.notes || "" }, rid);
  }, []);
  const deleteSupplier = useCallback(async (_rid: string, sid: string) => {
    const { suppliersApi } = await import("@/lib/suppliersApi");
    await suppliersApi.remove(sid);
  }, []);

  // --- Ingredients ---
  const fetchIngredients = useCallback(async (rid: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_ingredients", restaurant_id: rid, admin_session_id: adminSession },
      });
      return (data?.data || []) as Ingredient[];
    }
    const { data } = await supabase.from("ingredients").select("*").eq("restaurant_id", rid).order("created_at");
    return data || [];
  }, []);
  const addIngredient = useCallback(async (rid: string, ing: { name: string }) => {
    await supabase.from("ingredients").insert({ restaurant_id: rid, name: ing.name });
  }, []);
  const deleteIngredient = useCallback(async (_rid: string, iid: string) => {
    // Check if ingredient is still linked to any component
    const { count } = await supabase.from("component_ingredients").select("id", { count: "exact", head: true }).eq("ingredient_id", iid);
    if (!count || count === 0) {
      await supabase.from("ingredient_suppliers").delete().eq("ingredient_id", iid);
    }
    await supabase.from("ingredients").delete().eq("id", iid);
  }, []);

  // --- Users (via edge function) ---
  const fetchUsers = useCallback(async (rid: string) => {
    const { data } = await supabase.functions.invoke("admin-manage", {
      body: { action: "list_restaurant_users", restaurant_id: rid, admin_session_id: getAdminSessionId() },
    });
    return data?.data || [];
  }, []);
  const addUser = useCallback(async (rid: string, u: { username: string; password: string; role: string }) => {
    if (!u.password || u.password.length < 8) throw new Error("Password must be at least 8 characters");
    await supabase.functions.invoke("admin-manage", {
      body: { action: "create_restaurant_user", restaurant_id: rid, username: u.username, password: u.password, role: u.role, admin_session_id: getAdminSessionId() },
    });
  }, []);
  const deleteUser = useCallback(async (_rid: string, uid: string) => {
    await supabase.functions.invoke("admin-manage", {
      body: { action: "delete_restaurant_user", id: uid, admin_session_id: getAdminSessionId() },
    });
  }, []);
  const toggleUserStatus = useCallback(async (_rid: string, uid: string) => {
    await supabase.functions.invoke("admin-manage", {
      body: { action: "toggle_restaurant_user_status", id: uid, admin_session_id: getAdminSessionId() },
    });
  }, []);

  // --- Settings (routed through manage-restaurant-settings Edge Function) ---
  const fetchSettings = useCallback(async (rid: string) => {
    const { restaurantSettingsApi } = await import("@/lib/restaurantSettingsApi");
    return await restaurantSettingsApi.get(rid);
  }, []);
  const saveSettings = useCallback(async (rid: string, s: { language: string; integration_key: string }) => {
    const { restaurantSettingsApi } = await import("@/lib/restaurantSettingsApi");
    await restaurantSettingsApi.save(rid, s);
  }, []);

  // ========== JUNCTION TABLE CRUD ==========

  // Menu ↔ Dishes
  const fetchMenuDishes = useCallback(async (menuId: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_menu_dishes", menu_id: menuId, admin_session_id: adminSession },
      });
      return (data?.data || []) as Dish[];
    }
    const { data } = await supabase
      .from("menu_dishes")
      .select("dish_id")
      .eq("menu_id", menuId);
    if (!data || data.length === 0) return [];
    const ids = data.map((r: any) => r.dish_id);
    const { data: dishes } = await supabase.from("dishes").select("*").in("id", ids);
    return dishes || [];
  }, []);
  const addMenuDish = useCallback(async (menuId: string, dishId: string) => {
    await supabase.from("menu_dishes").insert({ menu_id: menuId, dish_id: dishId });
  }, []);
  const removeMenuDish = useCallback(async (menuId: string, dishId: string) => {
    await supabase.from("menu_dishes").delete().eq("menu_id", menuId).eq("dish_id", dishId);
  }, []);

  // Dish ↔ Components
  const fetchDishComponents = useCallback(async (dishId: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_dish_components", dish_id: dishId, admin_session_id: adminSession },
      });
      return (data?.data || []) as Component[];
    }
    const { data } = await supabase
      .from("dish_components")
      .select("component_id")
      .eq("dish_id", dishId);
    if (!data || data.length === 0) return [];
    const ids = data.map((r: any) => r.component_id);
    const { data: components } = await supabase.from("components").select("*").in("id", ids);
    return components || [];
  }, []);
  const addDishComponent = useCallback(async (dishId: string, componentId: string) => {
    await supabase.from("dish_components").insert({ dish_id: dishId, component_id: componentId });
  }, []);
  const removeDishComponent = useCallback(async (dishId: string, componentId: string) => {
    await supabase.from("dish_components").delete().eq("dish_id", dishId).eq("component_id", componentId);
  }, []);

  // Component ↔ Ingredients
  const fetchComponentIngredients = useCallback(async (componentId: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_component_ingredients", component_id: componentId, admin_session_id: adminSession },
      });
      return (data?.data || []) as Ingredient[];
    }
    const { data } = await supabase
      .from("component_ingredients")
      .select("ingredient_id")
      .eq("component_id", componentId);
    if (!data || data.length === 0) return [];
    const ids = data.map((r: any) => r.ingredient_id);
    const { data: ingredients } = await supabase.from("ingredients").select("*").in("id", ids);
    return ingredients || [];
  }, []);
  const addComponentIngredient = useCallback(async (componentId: string, ingredientId: string) => {
    await supabase.from("component_ingredients").insert({ component_id: componentId, ingredient_id: ingredientId });
  }, []);
  const removeComponentIngredient = useCallback(async (componentId: string, ingredientId: string) => {
    await supabase.from("component_ingredients").delete().eq("component_id", componentId).eq("ingredient_id", ingredientId);
  }, []);

  // Ingredient ↔ Suppliers
  const fetchIngredientSuppliers = useCallback(async (ingredientId: string) => {
    const adminSession = getAdminSessionId();
    if (adminSession) {
      const { data } = await supabase.functions.invoke("admin-manage", {
        body: { action: "list_ingredient_suppliers", ingredient_id: ingredientId, admin_session_id: adminSession },
      });
      return (data?.data || []) as Supplier[];
    }
    const { data } = await supabase
      .from("ingredient_suppliers")
      .select("supplier_id")
      .eq("ingredient_id", ingredientId);
    if (!data || data.length === 0) return [];
    const ids = data.map((r: any) => r.supplier_id);
    const { suppliersApi } = await import("@/lib/suppliersApi");
    return await suppliersApi.getByIds(ids);
  }, []);
  const addIngredientSupplier = useCallback(async (ingredientId: string, supplierId: string) => {
    await supabase.from("ingredient_suppliers").insert({ ingredient_id: ingredientId, supplier_id: supplierId });
  }, []);
  const removeIngredientSupplier = useCallback(async (ingredientId: string, supplierId: string) => {
    await supabase.from("ingredient_suppliers").delete().eq("ingredient_id", ingredientId).eq("supplier_id", supplierId);
  }, []);

  // Dashboard aggregates
  const getTotalDishes = useCallback(async () => {
    const { count } = await supabase.from("dishes").select("*", { count: "exact", head: true });
    return count || 0;
  }, []);
  const getTotalUsers = useCallback(async () => {
    if (!getAdminSessionId()) return 0;
    const { data } = await supabase.functions.invoke("admin-manage", {
      body: { action: "count_restaurant_users", admin_session_id: getAdminSessionId() },
    });
    return data?.count || 0;
  }, []);

  return (
    <RestaurantContext.Provider value={{
      list, loading, refresh,
      addRestaurant, updateRestaurant, deleteRestaurant, fetchRestaurant,
      fetchMenus, addMenu, deleteMenu,
      fetchDishes, addDish, deleteDish,
      fetchComponents, addComponent, deleteComponent,
      fetchIngredients, addIngredient, deleteIngredient,
      fetchSuppliers, addSupplier, deleteSupplier,
      fetchUsers, addUser, deleteUser, toggleUserStatus,
      fetchSettings, saveSettings,
      fetchMenuDishes, addMenuDish, removeMenuDish,
      fetchDishComponents, addDishComponent, removeDishComponent,
      fetchComponentIngredients, addComponentIngredient, removeComponentIngredient,
      fetchIngredientSuppliers, addIngredientSupplier, removeIngredientSupplier,
      getTotalDishes, getTotalUsers,
    }}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurants() {
  const ctx = useContext(RestaurantContext);
  if (!ctx) throw new Error("useRestaurants must be used within RestaurantProvider");
  return ctx;
}
